import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import baseUrl from '../../base_url';
import Sidebar from './components/sidebar';
import axios from 'axios';

const OrderStatusDropdown = ({ orderId, currentStatus, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const dropdownRef = React.useRef(null);
  
  const statusOptions = [
    "Placed","Processing" ,"Shipped", "Delivered", "Canceled", "Refunded"
  ];
  
  // Handle status color
  const getStatusColor = (status) => {
    if (["Canceled", "Returned", "Refunded"].includes(status)) {
      return "red";
    } else if (["Delivered", "Completed"].includes(status)) {
      return "green";
    } else if (["Shipped", "Out for Delivery"].includes(status)) {
      return "blue";
    } else {
      return "yellow";
    }
  };
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle status update
  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === selectedStatus || isUpdating) {
      setIsOpen(false);
      return;
    }
    
    setIsUpdating(true);
    try {
      const response = await axios.post(
        `${baseUrl}api/admin/edit-status/${orderId}`, 
        { newStatus: newStatus }, 
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        setSelectedStatus(newStatus);
        if (onStatusChange) {
          onStatusChange(newStatus);
        }
      }
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status');
    } finally {
      setIsUpdating(false);
      setIsOpen(false);
    }
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-4 py-2 bg-white border rounded shadow-sm flex items-center justify-between min-w-40 text-${getStatusColor(selectedStatus)}-600 border-${getStatusColor(selectedStatus)}-300 hover:bg-${getStatusColor(selectedStatus)}-50 focus:outline-none`}
        disabled={isUpdating}
      >
        <span className="font-medium">
          {selectedStatus}
        </span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-5 w-5 ml-2 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 bg= rounded-md shadow-lg max-h-60 overflow-auto">
          {statusOptions.map((status) => (
            <button
              key={status}
              className={`w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none ${
                status === selectedStatus 
                  ? `bg-${getStatusColor(status)}-50 text-${getStatusColor(status)}-600 font-medium` 
                  : 'text-gray-700'
              }`}
              onClick={() => handleUpdateStatus(status)}
              disabled={isUpdating}
            >
              {status}
            </button>
          ))}
        </div>
      )}
      
      {isUpdating && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded">
        </div>
      )}
    </div>
  );
};



const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for tracking modal
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [trackingLink, setTrackingLink] = useState('');
  const [isSubmittingTracking, setIsSubmittingTracking] = useState(false);
  
  // State for status dropdown
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // ShipRocket related states
  const [shiprocketOrder, setShiprocketOrder] = useState(null);
  const [isCreatingShiprocketOrder, setIsCreatingShiprocketOrder] = useState(false);
  const [availableCouriers, setAvailableCouriers] = useState([]);
  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [isLoadingCouriers, setIsLoadingCouriers] = useState(false);
  const [isGeneratingAWB, setIsGeneratingAWB] = useState(false);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);
  const [isGeneratingManifest, setIsGeneratingManifest] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const [isShipRocketModalOpen, setIsShipRocketModalOpen] = useState(false);
  
  // Available status options
  const statusOptions = [
    "Placed", "Confirmed", "Shipped", "Out for Delivery", 
    "Delivered", "Canceled", "Refunded", "Returned", "Completed"
  ];

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await axios.get(`${baseUrl}api/admin/get-order/${id}`, { withCredentials: true });
        
        if (response.status !== 200) {
          throw new Error('Failed to fetch order details');
        }
        setOrder(response.data.data);
        
        if (response.data.data.shipRocketOrderId) {
          setShiprocketOrder({ id: response.data.data.shipRocketOrderId });
          fetchAvailableCouriers();
        }
        
        // If order has courier selected, set it
        if (response.data.data.courierName) {
          const foundCourier = availableCouriers.find(c => c.name === response.data.data.courierName);
          if (foundCourier) {
            setSelectedCourierId(foundCourier.id);
          }
        }
        
        // If order has tracking info, fetch tracking details
        if (response.data.data.tracking) {
          setTrackingLink(response.data.data.tracking);
          fetchTrackingInfo(response.data.data.tracking);
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  // Format date to a more readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Handle tracking submission
 const handleSubmitTracking = async (e) => {
    e.preventDefault();
    if (!trackingLink.trim()) return;
    
    setIsSubmittingTracking(true);
    try {
      const response = await axios.post(`${baseUrl}api/admin/add-tracking/${id}`, 
        { tracking : trackingLink }, 
        { withCredentials: true }
      );

      await axios.post(
        `${baseUrl}api/admin/edit-status/${id}`, 
        { newStatus: "Shipped" }, 
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        // Update the order locally
        setOrder(prev => ({
          ...prev,
          tracking: trackingLink
        }));
        // Close modal and reset input
        setIsTrackingModalOpen(false);
        setTrackingLink('');
      }
    } catch (err) {
      alert('Failed to add tracking information');
      console.error(err);
    } finally {
      setIsSubmittingTracking(false);
    }
  };
  
  // Handle status update
    const handleUpdateStatus = async (newStatus) => {
      if (newStatus === order.status) {
        setIsStatusDropdownOpen(false);
        return;
      }
      
      setIsUpdatingStatus(true);
      try {
        const response = await axios.post(
          `${baseUrl}api/admin/edit-status/${id}`, 
          { newStatus: newStatus }, // Changed from "status" to "newStatus"
          { withCredentials: true }
        );
        
        if (response.status === 200) {
          setOrder(prev => ({
            ...prev,
            status: newStatus
          }));
        }
      } catch (err) {
        alert('Failed to update order status');
        console.error(err);
      } finally {
        setIsUpdatingStatus(false);
        setIsStatusDropdownOpen(false);
      }
    };
    
// const createShipRocketOrder = async () => {
//   setIsCreatingShiprocketOrder(true);
//   try {
//     const response = await axios.post(
//       `${baseUrl}api/admin/orders/${id}/shiprocket`,
//       {},
//       { withCredentials: true }
//     );

    
//     if (response.data.statusCode === 200) {
//       const shiprocketData = response.data.data.data;
//       console.log(shiprocketData)
//       setShiprocketOrder(shiprocketData);

//       await axios.post(
//         `${baseUrl}api/admin/edit-status/${order._id}`,{ newStatus: "Processing" },{ withCredentials: true }
//       );

//       // setOrder((prev) => ({
//       //   ...prev,
//       //   shipRocketOrderId: shiprocketData.id,
//       //   status: "Processing", 
//       // }));
//       fetchAvailableCouriers(); 
//       refreshOrder();
//       alert("ShipRocket order created successfully!");
//     }
//   } catch (err) {
//     console.error("Failed to create ShipRocket order:", err);
//     alert(
//       "Failed to create ShipRocket order: " +
//         (err.response?.data?.message || err.message)
//     );
//   } finally {
//     setIsCreatingShiprocketOrder(false);
//   }
// };
  
  // Fetch available couriers
  

  const createShipRocketOrder = async () => {
    setIsCreatingShiprocketOrder(true);
    try {
      const response = await axios.post(
        `${baseUrl}api/admin/orders/${id}/shiprocket`,
        {},
        { withCredentials: true }
      );
  
      if (response.data.statusCode === 200) {
        const shiprocketData = response.data.data.data;
        console.log(shiprocketData)
        setShiprocketOrder(shiprocketData);
  
        await axios.post(
          `${baseUrl}api/admin/edit-status/${order._id}`,
          { newStatus: "Processing" },
          { withCredentials: true }
        );
  
        refreshOrder();
        alert("ShipRocket order created successfully!");
      }
    } catch (err) {
      console.error("Failed to create ShipRocket order:", err);
      alert(
        "Failed to create ShipRocket order: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setIsCreatingShiprocketOrder(false);
    }
  };
  
  const downloadInvoice = async () => {
    setIsGeneratingManifest(true);
    try {
      // First request to get the invoice URL
      const response = await axios.post(
        `${baseUrl}api/admin/orders/${id}/invoice`,
        {},
        { withCredentials: true }
      );
  
      const invoiceUrl = response.data.data.invoice_url;
      console.log('Invoice URL:', invoiceUrl);
  
      if (response.status === 200 && invoiceUrl) {
        // Second request to download the PDF
        const pdfResponse = await axios.get(invoiceUrl, {
          responseType: 'blob' // Important for handling binary PDF data
        });
  
        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `invoice_${id}.pdf`); // Set filename
        document.body.appendChild(link);
        link.click();
  
        // Clean up
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
  
        alert('Invoice downloaded successfully!');
      } else {
        throw new Error('No invoice URL received');
      }
    } catch (err) {
      console.error('Failed to download invoice:', err);
      alert('Failed to download invoice');
    } finally {
      setIsGeneratingManifest(false);
    }
  };
  
  
  const fetchAvailableCouriers = async () => {
    setIsLoadingCouriers(true);
    try {
      const response = await axios.get(
        `${baseUrl}api/admin/orders/${id}/couriers`,
        { withCredentials: true }
      );
      
      if (response.data.statusCode === 200) {
        const couriers = response.data.data.data.available_courier_companies || [];
        setAvailableCouriers(couriers);
        
        // If order has courierName but no selectedCourierId yet, try to find match
        if (order?.courierName && !selectedCourierId) {
          const foundCourier = couriers.find(c => c.name === order.courier_name);
          if (foundCourier) {
            setSelectedCourierId(foundCourier.id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch available couriers:', err);
      // alert('Failed to fetch available couriers');
    } finally {
      setIsLoadingCouriers(false);
    }
  };
  
  // Generate AWB number
const generateAWB = async () => {
  if (!selectedCourierId) {
    alert("Please select a courier first");
    return;
  }

  setIsGeneratingAWB(true);
  try {
    const response = await axios.post(
      `${baseUrl}api/admin/orders/${id}/generate-awb`,
      { courier_id: selectedCourierId },
      { withCredentials: true }
    );

    if (response.data.statusCode === 200) {
      const awbCode = response.data.awb_code;
      const courierName =
        availableCouriers.find((c) => c.id === selectedCourierId)?.name || "";

      // Update order state with tracking and courier info
      setOrder((prev) => ({
        ...prev,
        tracking: awbCode,
        courierName: courierName,
        status: "Shipped", // Update status if applicable
      }));

      alert("AWB generated successfully: " + awbCode);
      fetchTrackingInfo(awbCode); // Fetch tracking info immediately
      refreshOrder();
    }
  } catch (err) {
    console.error("Failed to generate AWB:", err);
    alert(
      "Failed to generate AWB: " + (err.response?.data?.message || err.message)
    );
  } finally {
    setIsGeneratingAWB(false);
  }
};
  
  // Fetch tracking information
 const fetchTrackingInfo = async (awbCode) => {
    if (!awbCode) return;
    
    setIsLoadingTracking(true);
    try {
      const response = await axios.get(
        `${baseUrl}api/admin/tracking/${awbCode}`,
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        setTrackingInfo(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch tracking info:', err);
      alert('Failed to fetch tracking info');
    } finally {
      setIsLoadingTracking(false);
    }
  };
  
  // Generate shipping label
  const generateLabel = async () => {
    setIsGeneratingLabel(true);
    try {
      const response = await axios.post(
        `${baseUrl}api/admin/orders/${id}/generate-label`,
        {},
        { withCredentials: true }
      );

      console.log(response)
      
      if (response.status === 200) {
        // Open the label URL in a new tab if available
        if (response.data.data && response.data.data.label_url) {
          window.open(response.data.data.label_url, '_blank');
        }
        alert('Shipping label generated successfully!');
      }
    } catch (err) {
      console.error('Failed to generate shipping label:', err);
      alert('Failed to generate shipping label');
    } finally {
      setIsGeneratingLabel(false);
    }
  };
  
  // Generate manifest
  const generateManifest = async () => {
    setIsGeneratingManifest(true);
    try {
      const response = await axios.post(
        `${baseUrl}api/admin/orders/${id}/generate-manifest`,
        {},
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        // Open the manifest URL in a new tab if available
        if (response.data.data && response.data.data.manifest_url) {
          window.open(response.data.data.manifest_url, '_blank');
        }
        alert('Manifest generated successfully!');
      }
    } catch (err) {
      console.error('Failed to generate manifest:', err);
      alert('Failed to generate manifest');
    } finally {
      setIsGeneratingManifest(false);
    }
  };

  // const downloadInvoice = async () => {
  //   setIsGeneratingManifest(true);
  //   try {
  //     // First request to get the invoice URL
  //     const response = await axios.post(
  //       `${baseUrl}api/admin/orders/${id}/invoice`,
  //       {},
  //       { withCredentials: true }
  //     );

  //     const invoiceUrl = response.data.data.invoice_url;
  //     console.log('Invoice URL:', invoiceUrl);

  //     if (response.status === 200 && invoiceUrl) {
  //       // Second request to download the PDF
  //       const pdfResponse = await axios.get(invoiceUrl, {
  //         responseType: 'blob' // Important for handling binary PDF data
  //       });

  //       // Create a blob URL and trigger download
  //       const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
  //       const link = document.createElement('a');
  //       link.href = url;
  //       link.setAttribute('download', `invoice_${id}.pdf`); // Set filename
  //       document.body.appendChild(link);
  //       link.click();

  //       // Clean up
  //       link.parentNode.removeChild(link);
  //       window.URL.revokeObjectURL(url);

  //       alert('Invoice downloaded successfully!');
  //     } else {
  //       throw new Error('No invoice URL received');
  //     }
  //   } catch (err) {
  //     console.error('Failed to download invoice:', err);
  //     alert('Failed to download invoice');
  //   } finally {
  //     setIsGeneratingManifest(false);
  //   }
  // };
    
  const renderShipRocketActions = () => {
    // If the order doesn't exist yet or is loading, don't render anything
    if (!order || loading) return null;
    
    // If ShipRocket order doesn't exist yet, show create button
    // if (!order.shipRocketOrderId && !shiprocketOrder) {
    //   return (
    //     <button 
    //       onClick={createShipRocketOrder}
    //       disabled={isCreatingShiprocketOrder}
    //       className="btn btn-primary"
    //     >
    //       {isCreatingShiprocketOrder ? 'Creating...' : 'Create ShipRocket Order'}
    //     </button>
    //   );
    // }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsStatusDropdownOpen(false);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const refreshOrder = async () => {
    // console.log(id)
  try {
    const response = await axios.get(`${baseUrl}api/admin/get-order/${id}`, {
      withCredentials: true,
    });

    if (response.status === 200) {
      setOrder(response.data.data);
      if (response.data.data.shipRocketOrderId) {
        setShiprocketOrder({ id: response.data.data.shipRocketOrderId });
        fetchAvailableCouriers();
      }
      if (response.data.data.tracking) {
        setTrackingLink(response.data.data.tracking);
        fetchTrackingInfo(response.data.data.tracking);
      }
    }
  } catch (err) {
    console.error("Failed to refresh order:", err);
    setError(err.message);
  }
};

  const TrackingModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">
              {order.tracking !== "NA" ? "Update Tracking" : "Add Tracking"}
            </h3>
            <button 
              onClick={() => setIsTrackingModalOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmitTracking}>
            <div className="mb-4">
              <label htmlFor="tracking" className="block text-gray-700 mb-2">
                Tracking Link
              </label>
              <input
                id="tracking"
                type="text"
                value={trackingLink}
                onChange={(e) => setTrackingLink(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter tracking link or number"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setIsTrackingModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingTracking || !trackingLink.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {isSubmittingTracking ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

// const ShipRocketModal = () => {
//   console.log(shiprocketOrder);

//   // Auto-fetch couriers when order is created
//   useEffect(() => {
//       if (shiprocketOrder && availableCouriers.length === 0) {
//         fetchAvailableCouriers();
//       }
//     }, [shiprocketOrder, availableCouriers.length]);

//   // Auto-fetch tracking info when AWB is generated
//   useEffect(() => {
//       if (order?.tracking && order.tracking !== "NA" && !trackingInfo) {
//         fetchTrackingInfo(order.tracking);
//       }
//     }, [order?.tracking, trackingInfo]);

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
//         <div className="flex justify-between items-center mb-4">
//           {/* <h3 className="text-xl font-semibold">ShipRocket Integration</h3> */}
//           <button 
//             onClick={() => setIsShipRocketModalOpen(false)}
//             className="text-gray-500 hover:text-gray-700"
//           >
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//             </svg>
//           </button>
//         </div>

//         <div className="space-y-4">
//           {/* Step 1: Create ShipRocket Order */}
//           <div className="border border-gray-200 rounded-md p-4">
//             <h4 className="font-medium mb-2">Step 1: Create ShipRocket Order</h4>
//             {shiprocketOrder ? (
//               <div className="bg-green-50 border border-green-200 rounded p-3 text-green-700">
//                 <p className="font-medium">Order created in ShipRocket</p>
//                 <p className="text-sm">ShipRocket Order ID: {shiprocketOrder.id}</p>
//               </div>
//             ) : (
//               <button
//                 onClick={createShipRocketOrder}
//                 disabled={isCreatingShiprocketOrder}
//                 className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
//               >
//                 {isCreatingShiprocketOrder ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Creating...
//                   </>
//                 ) : (
//                   "Create ShipRocket Order"
//                 )}
//               </button>
//             )}
//           </div>

//           {/* Step 2: Select Courier */}
//           {shiprocketOrder && (!order.tracking || order.tracking === "NA") && (
//             <div className="border border-gray-200 rounded-md p-4">
//               <h4 className="font-medium mb-2">Step 2: Select Courier</h4>
//               {availableCouriers.length > 0 ? (
//                 <div className="space-y-3">
//                   <select
//                     value={selectedCourierId}
//                     onChange={(e) => setSelectedCourierId(e.target.value)}
//                     className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   >
//                     <option value="">-- Select Courier --</option>
//                     {availableCouriers
//                       .slice()
//                       .sort((a, b) => a.estimated_delivery_days - b.estimated_delivery_days)
//                       .map((courier) => (
//                         <option key={courier.courier_company_id} value={courier.courier_company_id}>
//                           {courier.courier_name} - ₹{courier.rate} ({courier.etd}), {courier.estimated_delivery_days} days
//                         </option>
//                       ))}
//                   </select>
//                   {selectedCourierId && (
//                     <button
//                       onClick={generateAWB}
//                       disabled={isGeneratingAWB}
//                       className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
//                     >
//                       {isGeneratingAWB ? (
//                         <>
//                           <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                           </svg>
//                           Generating...
//                         </>
//                       ) : (
//                         "Generate AWB"
//                       )}
//                     </button>
//                   )}
//                 </div>
//               ) : (
//                 <div className="flex space-x-2">
//                   <button
//                     onClick={fetchAvailableCouriers}
//                     disabled={isLoadingCouriers}
//                     className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
//                   >
//                     {isLoadingCouriers ? (
//                       <>
//                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                         </svg>
//                         Loading...
//                       </>
//                     ) : (
//                       "Fetch Available Couriers"
//                     )}
//                   </button>
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Step 3: Generate Label & Manifest (available once AWB is generated) */}
//           {order && order.tracking && order.tracking !== "NA" && (
//             <div className="border border-gray-200 rounded-md p-4">
//               <h4 className="font-medium mb-2">Step 3: Generate Shipping Documents</h4>
//               <div className="flex flex-col space-y-2">
//                 <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
//                   <p className="font-medium">AWB Number: {order.tracking}</p>
//                   {order.courierName && <p className="text-sm">Courier: {order.courierName}</p>}
//                 </div>
                
//                 <div className="flex space-x-2">
//                   <button
//                     onClick={generateLabel}
//                     disabled={isGeneratingLabel}
//                     className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
//                   >
//                     {isGeneratingLabel ? (
//                       <>
//                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                         </svg>
//                         Generating...
//                       </>
//                     ) : (
//                       "Generate Shipping Label"
//                     )}
//                   </button>
                  
//                   <button
//                     onClick={generateManifest}
//                     disabled={isGeneratingManifest}
//                     className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center"
//                   >
//                     {isGeneratingManifest ? (
//                       <>
//                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                         </svg>
//                         Generating...
//                       </>
//                     ) : (
//                       "Generate Manifest"
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Step 4: Tracking Information */}
//           {order && order.tracking && order.tracking !== "NA" && (
//             <div className="border border-gray-200 rounded-md p-4">
//               <h4 className="font-medium mb-2">Step 4: Tracking Information</h4>
//               <button
//                 onClick={() => fetchTrackingInfo(order.tracking)}
//                 disabled={isLoadingTracking}
//                 className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center mb-3"
//               >
//                 {isLoadingTracking ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Fetching...
//                   </>
//                 ) : (
//                   "Refresh Tracking Info"
//                 )}
//               </button>

//               {trackingInfo ? (
//                 <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
//                   <div className="bg-gray-50 px-4 py-2 border-b">
//                     <h5 className="font-medium">Tracking History</h5>
//                   </div>
//                   <div className="p-4">
//                     {trackingInfo.tracking_data && trackingInfo.tracking_data.shipment_track ? (
//                       <div className="space-y-3">
//                         <div className="flex justify-between text-sm text-gray-600">
//                           <span>Current Status:</span>
//                           <span className="font-medium text-blue-600">
//                             {trackingInfo.tracking_data.shipment_track[0]?.current_status || "Not Available"}
//                           </span>
//                         </div>
                        
//                         <div className="space-y-2">
//                           {trackingInfo.tracking_data.shipment_track_activities ? (
//                             trackingInfo.tracking_data.shipment_track_activities.map((activity, idx) => (
//                               <div key={idx} className="border-l-2 border-blue-500 pl-3 pb-3">
//                                 <p className="text-sm font-medium">{activity.activity}</p>
//                                 <p className="text-xs text-gray-500">
//                                   {new Date(activity.date).toLocaleString()}
//                                 </p>
//                                 <p className="text-xs text-gray-500">{activity.location}</p>
//                               </div>
//                             ))
//                           ) : (
//                             <p className="text-sm text-gray-500">No tracking activities available</p>
//                           )}
//                         </div>
//                       </div>
//                     ) : (
//                       <p className="text-sm text-gray-500">No tracking data available</p>
//                     )}
//                   </div>
//                 </div>
//               ) : (
//                 <p className="text-sm text-gray-500">Loading tracking information...</p>
//               )}
//             </div>
//           )}
//         </div>

//         <div className="mt-6 flex justify-end">
//           <button
//             onClick={() => setIsShipRocketModalOpen(false)}
//             className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };
const ShipRocketModal = () => {
  console.log(shiprocketOrder);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={() => setIsShipRocketModalOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Step 1: Create ShipRocket Order */}
          <div className="border border-gray-200 rounded-md p-4">
            <h4 className="font-medium mb-2">Create ShipRocket Order</h4>
            {shiprocketOrder ? (
              <div className="bg-green-50 border border-green-200 rounded p-3 text-green-700">
                <p className="font-medium">Order created in ShipRocket</p>
                <p className="text-sm">ShipRocket Order ID: {shiprocketOrder.id}</p>
              </div>
            ) : (
              <button
                onClick={createShipRocketOrder}
                disabled={isCreatingShiprocketOrder}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
              >
                {isCreatingShiprocketOrder ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  "Create ShipRocket Order"
                )}
              </button>
            )}
          </div>

          {/* Download Invoice */}
          <div className="border border-gray-200 rounded-md p-4">
            <h4 className="font-medium mb-2">Download Invoice</h4>
            <button
              onClick={downloadInvoice}
              disabled={isGeneratingManifest}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
            >
              {isGeneratingManifest ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Downloading...
                </>
              ) : (
                "Download Invoice"
              )}
            </button>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={() => setIsShipRocketModalOpen(false)}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
  
  if (loading) return <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8">Loading order details...</div></div>;
  if (error) return <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8">Error: {error}</div></div>;
  if (!order) return <div className="flex min-h-screen"><Sidebar /><div className="flex-1 p-8">No order found with ID: {id}</div></div>;
  console.log(order)
  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1">
        {/* Header with search */}

        {/* Content */}
        <div className="p-6">
          {/* Back button and Order ID */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <a href="/admin/orders" className="text-gray-600 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Orders
              </a>
            </div>
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-700">#{order._id.slice(-6)}</h2>
              
              <div className="text-sm text-gray-600">
                {formatDate(order.createdAt)} from Online Store
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mb-6 flex space-x-2">
            <button onClick={downloadInvoice} className="px-4 py-2 bg-white border border-gray-300 rounded flex items-center text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <button className="px-4 py-2 bg-white border border-gray-300 rounded flex items-center text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refund
            </button>
            <button className="px-4 py-2 bg-white border border-gray-300 rounded flex items-center text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button 
              className="px-4 py-2 bg-blue-600 border border-blue-700 rounded flex items-center text-white hover:bg-blue-700"
              onClick={() => setIsShipRocketModalOpen(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              ShipRocket
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order information */}
            <div className="lg:col-span-2">
              {/* Order status section */}
              <div className="bg-white rounded-lg shadow mb-6 p-6">
                <div className="flex items-center mb-4">
                  <div className={`bg-${order.status === 'Canceled' || order.status === 'Returned' ? 'red' : 'green'}-100 rounded-full p-1 mr-2`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 text-${order.status === 'Canceled' || order.status === 'Returned' ? 'red' : 'green'}-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {order.status === 'Canceled' || order.status === 'Returned' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      )}
                    </svg>
                  </div>
                  <div className="flex items-center">
                    <div className="relative mr-1">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsStatusDropdownOpen(!isStatusDropdownOpen);
                        }}
                        className={`font-medium focus:outline-none flex items-center cursor-auto ${
                          order.status === 'Canceled' || order.status === 'Returned' ? 'text-red-600' : 'text-green-600'
                        }`}
                        disabled={isUpdatingStatus}
                      >
                        Status: {order.status}
                        {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg> */}
                      </button>
                      
                      {isStatusDropdownOpen && (
                        <div 
                          className="absolute z-10 mt-1 bg-white shadow-lg rounded-md py-1 w-40 max-h-60 overflow-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                        </div>
                      )}
                    </div>
                    <span className="ml-2 text-gray-500">#{order._id}</span>
                    <p>{order.articleCode}</p>
                  </div>
                </div>

                {/* ShipRocket status */}
                {/* <div className="mt-6">
                  <h3 className="text-lg font-medium mb-4">ShipRocket Integration</h3>
                  {renderShipRocketActions()}
                </div> */}


                {/* Tracking information */}
                <div className="mb-4">
                  <p className="text-gray-700">
                    Tracking Link
                    <br />
                    {order.tracking !== "NA" ? (
                      <a href={order.tracking} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{order.tracking}</a>
                    ) : (
                      <span className="text-gray-500 italic">No tracking information available</span>
                    )}
                  </p>
                </div>

                {/* Order items */}
                <div className="space-y-4">
                  {order.cart.map((item, index) => (
                    <div key={index} className="border-t pt-4">
                      <div className="flex">
                        <div className="flex-shrink-0 mr-4 relative">
                          <img src={item.image || "/placeholder.png"} alt={item.title} className="w-16 h-16 object-cover border rounded" />
                          <div className="absolute -top-2 -left-2 bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                            {item.quantity}
                          </div>
                        </div>
                        <div className="flex-grow">
                          <a href={`/productpage/${item.productId}`} className="text-blue-500">{item.title}</a>
                          <div className="flex justify-between mt-2">
                            <div className="flex items-center">
                              <span className="text-gray-700">₹  {(item.price).toFixed(2)}</span>
                              <span className="mx-2 text-gray-500">×</span>
                              <span className="text-gray-700">{item.quantity}</span>
                            </div>
                            <span className="text-gray-700">₹  {(item.cartValue).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="mt-6 flex space-x-2">
                  <div className="flex items-center mb-4">
                    <OrderStatusDropdown 
                      orderId={order._id}
                      currentStatus={order.status}
                      onStatusChange={(newStatus) => {
                        setOrder(prev => ({
                          ...prev,
                          status: newStatus
                        }));
                      }}
                    />
                  </div>
                  <button 
                    className="px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors max-h-[42px]"
                    onClick={() => setIsTrackingModalOpen(true)}
                  >
                    {order.tracking !== "NA" ? "Update Tracking" : "Add Tracking"}
                  </button>
                  <button 
                    className="px-4 py-2 bg-blue-600 text-white border border-blue-600 rounded hover:bg-blue-700 transition-colors max-h-[42px]"
                    onClick={() => setIsShipRocketModalOpen(true)}
                  >
                    ShipRocket
                  </button>
                </div>
              </div>

              {/* Payment section */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center mb-4 text-green-600">
                  <div className="bg-green-100 rounded-full p-1 mr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-medium">Paid</span>
                </div>

                {/* Order summary */}
                <div className="space-y-2 border-b pb-4 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <div className="text-right">
                      <div>{order.cart.length} item{order.cart.length > 1 ? 's' : ''}</div>
                      <div className="text-gray-800">₹  {(order.totalAmount).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Standard (0.0kg)</div>
                      <div className="text-gray-800">₹  0.00</div>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">VAT 0%</div>
                      <div className="text-gray-800">₹  0.00</div>
                    </div>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span className="text-gray-800">Total</span>
                    <span className="text-gray-800">₹  {(order.totalAmount).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-between text-gray-800">
                  <span>Paid by Customer</span>
                  <span>₹  {(order.totalAmount).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Customer information */}
            <div className="space-y-6">
              {/* Notes */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-700 font-medium mb-2">Notes</h3>
                <p className="text-gray-600">
                  No notes from the Customer
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-gray-700 font-medium">Customer</h3>
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-700 mb-1">{order.address[0]?.fullName}</p>

                <div className="mt-6">
                  <h4 className="text-gray-700 font-medium mb-2">CONTACT INFORMATION</h4>
                  <p className="text-gray-600 mb-1">Customer ID: {order.user}</p>
                  <p className="text-gray-600 mb-6">{order.address[0]?.phone || "No phone number"}</p>

                  <h4 className="text-gray-700 font-medium mb-2">SHIPPING ADDRESS</h4>
                  <p className="text-gray-600">
                    {order.address[0]?.fullName}<br />
                    {
                      order.address[0]?.houseNumber && 
                      <>
                        {order.address[0]?.houseNumber} <br></br>
                      </>
                    }
                    {order.address[0]?.streetAddress}<br />
                    {order.address[0]?.city}, {order.address[0]?.state} {order.address[0]?.postalCode}<br />
                    {order.address[0]?.country}
                  </p>

                  <h4 className="text-gray-700 font-medium mt-6 mb-2">BILLING ADDRESS</h4>
                  <p className="text-gray-600">
                    Same as Shipping address
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isTrackingModalOpen && <TrackingModal />}
      {isShipRocketModalOpen && <ShipRocketModal />}
    </div>
  );
};

export default OrderDetails